import { FontUtils, Image3D, LayoutUtils, MeshUtils, ObjectBuilder, Rectangle3D, TabControl } from "@dreamcam/babylon-tools";
import { Env } from "../../settings/Env";
import { DcRenderingGroup } from "../DcRenderingGroup";
import { createVector3 } from "../ButtonAnimation";
import { Vector3 } from "@babylonjs/core/Maths/math.vector.js";
import { AppView } from "../AppView";
import { Control } from "@babylonjs/gui/2D/controls/control.js";
import { text } from "../../TextResources";
import { Container } from "@babylonjs/gui/2D/controls/container.js";
import { XRInputHelper } from "../XRInputHelper";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel.js";
import { LiveTagsPanel } from "./LiveTagsPanel";
import { VideoTagsPanel } from "./VideoTagsPanel";
import { VideoTagsPurchasesPanel } from "./VideoTagsPurchasesPanel";
export class LobbyTagsPanel {
    get mesh2d() { return this._mesh2d; }
    ;
    constructor(meshName, gallerySize, categories, callbacks, lobbyTagsPanelCallbacks) {
        this._appView = AppView.getInstance();
        this._callbacks = callbacks;
        this._lobbyTagsPanelCallbacks = lobbyTagsPanelCallbacks;
        this._mesh2d = this._createMesh(meshName, gallerySize);
        const contentContainers = this._createLobbyModePanel(gallerySize);
        this._tabControl = contentContainers.tabControl;
        this._liveTagsPanel = new LiveTagsPanel(contentContainers.liveContentContainer, this._mesh2d, categories, callbacks);
        this._videoTagsPanel = new VideoTagsPanel(contentContainers.videosContentContainer, this._mesh2d, callbacks);
        this._videoTagsPurchasesPanel = new VideoTagsPurchasesPanel(contentContainers.videoPurchasesContentContainer, this._mesh2d);
        const pointerAddon = XRInputHelper.addPointerAddon(this._mesh2d.mesh);
        pointerAddon.onPointerEnter.add(() => {
            this._liveTagsPanel.updateScrollOnPointerEnter();
            this._videoTagsPanel.updateScrollOnPointerEnter();
        });
        pointerAddon.onPointerOut.add(() => {
            this._liveTagsPanel.updateScrollOnPointerOut();
            this._videoTagsPanel.updateScrollOnPointerOut();
            this._mesh2d.notifyChildrenOnPointerOut();
        });
    }
    show() {
        this._tabControl.currentTab = this._callbacks?.getLobbyMode() === "Videos" ? 1 : 0;
        MeshUtils.show(this._mesh2d);
    }
    hide() {
        MeshUtils.hide(this._mesh2d);
    }
    enableTabControl() {
        this._tabControl.isEnabled = true;
    }
    disableTabControl() {
        this._tabControl.isEnabled = false;
    }
    updateLiveTagGroups(groups) {
        this._liveTagsPanel.updateTagGroups(groups);
    }
    updateVideosTags(tags) {
        this._videoTagsPanel.updateTags(tags);
    }
    setPanelsVisibility(visibility) {
        this._liveTagsPanel.isVisible = visibility.liveTagsPanelVisible;
        this._videoTagsPanel.isVisible = visibility.videoTagsPanelVisible;
        this._videoTagsPurchasesPanel.isVisible = visibility.videoTagsPurchasesPanelVisible;
    }
    _createMesh(name, gallerySize) {
        const tp = Env.lobby.tagsPanel;
        const width = tp.width;
        const result = MeshUtils.createPlaneMesh(name, width, gallerySize.height, this._appView.scene, this._appView.guiRootNode, DcRenderingGroup.default);
        result.mesh.position = createVector3(tp.mesh.panelPosition);
        XRInputHelper.addPointerAddon(result.mesh).onPointerOut.add(() => result.notifyChildrenOnPointerOut());
        const galleryHalfAngle = gallerySize.angularWidth / 2;
        const panelHalfAngle = Math.atan2(width / 2, tp.mesh.panelPosition.z);
        const rotationAngle = galleryHalfAngle + panelHalfAngle + tp.mesh.rotationAnglePadding * Math.PI / 180;
        result.mesh.rotateAround(Vector3.Zero(), Vector3.Up(), -rotationAngle);
        return result;
    }
    _createLobbyModePanel(gallerySize) {
        const tp = Env.lobby.tagsPanel;
        const panelBackground = new Rectangle3D("lobbyTagsPanelBackground");
        panelBackground.background = tp.mesh.background;
        panelBackground.cornerRadius = this._mesh2d.m2px(tp.mesh.cornerRadius);
        this._mesh2d.texture.addControl(panelBackground);
        const liveContentContainer = new Container("liveContentContainer");
        liveContentContainer.heightInPixels = this._mesh2d.m2px(gallerySize.height - tp.lobbyMode.tabs.top - tp.lobbyMode.tabs.height);
        liveContentContainer.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
        liveContentContainer.paddingLeftInPixels = this._mesh2d.m2px(tp.liveTags.padding.left);
        liveContentContainer.paddingRightInPixels = this._mesh2d.m2px(tp.liveTags.padding.right);
        panelBackground.addControl(liveContentContainer);
        const videosContentContainer = new Container("videosContentContainer");
        videosContentContainer.heightInPixels = this._mesh2d.m2px(gallerySize.height - tp.lobbyMode.tabs.top - tp.lobbyMode.tabs.height);
        videosContentContainer.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
        videosContentContainer.paddingLeftInPixels = this._mesh2d.m2px(tp.videoTags.padding.left);
        videosContentContainer.paddingRightInPixels = this._mesh2d.m2px(tp.videoTags.padding.right);
        panelBackground.addControl(videosContentContainer);
        const videoPurchasesContentContainer = new Container("videoPurchasesContentContainer");
        videoPurchasesContentContainer.heightInPixels = this._mesh2d.m2px(gallerySize.height - tp.lobbyMode.tabs.top - tp.lobbyMode.tabs.height);
        videoPurchasesContentContainer.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
        videoPurchasesContentContainer.paddingLeftInPixels = this._mesh2d.m2px(tp.videoTags.padding.left);
        videoPurchasesContentContainer.paddingRightInPixels = this._mesh2d.m2px(tp.videoTags.padding.right);
        panelBackground.addControl(videoPurchasesContentContainer);
        const lm = tp.lobbyMode;
        const tabControlBackground = new Rectangle3D("tabControlBackground");
        tabControlBackground.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        tabControlBackground.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        tabControlBackground.widthInPixels = this._mesh2d.m2px(lm.tabs.width);
        tabControlBackground.heightInPixels = this._mesh2d.m2px(lm.tabs.height);
        tabControlBackground.topInPixels = this._mesh2d.m2px(lm.tabs.top);
        tabControlBackground.background = lm.tabs.background.color;
        tabControlBackground.cornerRadius = this._mesh2d.m2px(lm.tabs.background.cornerRadius);
        panelBackground.addControl(tabControlBackground);
        const tabControl = new TabControl("lobbyModeTabs");
        // tabControl.background = "transparent";
        tabControlBackground.addControl(tabControl);
        const modes = ["Live", "Videos"];
        modes.forEach((mode, index) => {
            const tabRectangle = new Rectangle3D(`tab${mode}Rectangle`);
            tabRectangle.widthInPixels = this._mesh2d.m2px(lm.tabs.tab.width);
            tabRectangle.heightInPixels = this._mesh2d.m2px(lm.tabs.height);
            tabRectangle.thickness = 0;
            tabControl.addControl(null, tabRectangle, LayoutUtils.GetButtonLayout(lm.tabs.tab.buttonLayout, this._mesh2d));
            const stackPanel = new StackPanel(`tab${mode}StackPanel`);
            stackPanel.isVertical = false;
            tabRectangle.addControl(stackPanel);
            const tabImage = new Image3D(`tab${mode}Image`, mode === "Live" ? lm.tabs.images.liveImg : lm.tabs.images.videosImg);
            tabImage.widthInPixels = this._mesh2d.m2px(lm.tabs.images.width);
            tabImage.heightInPixels = this._mesh2d.m2px(lm.tabs.images.height);
            stackPanel.addControl(tabImage);
            const tabCaption = ObjectBuilder.createTextBlock3D(this._mesh2d, `tab${mode}Text`, mode === "Live" ? text.lobby_mode_live : text.lobby_mode_videos, lm.tabs.tab.fontStyle);
            tabCaption.stateController.setStates(lm.tabs.tab.states.map(s => {
                return { state: s.state, style: FontUtils.getAdjustedFontStyle(s.style, this._mesh2d) };
            }));
            tabCaption.heightInPixels = this._mesh2d.m2px(lm.tabs.height);
            if (index === 0) {
                setTimeout(() => tabRectangle.stateController.changeState("toggled", true), 0);
            }
            stackPanel.addControl(tabCaption);
        });
        tabControl.currentTab = this._callbacks?.getLobbyMode() === "Videos" ? 1 : 0;
        tabControl.onTabChangedObservable.add((index) => {
            const lobbyMode = index === 0 ? "Live" : "Videos";
            this._lobbyTagsPanelCallbacks.onLobbyModeChanged(lobbyMode);
            // this._liveGallery?.setVisibility(this._lobbyMode === "Live");
            // this._videoGallery?.setVisibility(this._lobbyMode === "Videos");
            //
            // if (this._liveTagsPanel) {
            //     this._liveTagsPanel.isVisible = this._lobbyMode === "Live";
            // }
            // if (this._videoTagsPanel) {
            //     this._videoTagsPanel.isVisible = this._lobbyMode === "Videos";
            // }
            //
            // if (this._upcomingEvent) {
            //     MeshUtils.setVisibility(this._upcomingEvent.header, this._lobbyMode === "Live");
            //     MeshUtils.setVisibility(this._upcomingEvent.panel, this._lobbyMode === "Live");
            // }
            //
            // MeshUtils.setVisibility(this._liveTabsMesh, this._lobbyMode === "Live");
            // MeshUtils.setVisibility(this._videoTabsMesh, this._lobbyMode === "Videos");
            //
            // this.updateModelMode();
            //
            // if (this._callbacks) {
            //     const livePaginationInfo = this._callbacks.getLivePaginationInfo();
            //     this.drawBroadcastsPageInfo(livePaginationInfo.totalPages, livePaginationInfo.currentPageIndex);
            //     const videosPaginationInfo = this._callbacks.getVideosPaginationInfo();
            //     this.drawVideoClipsPageInfo(videosPaginationInfo.totalPages, videosPaginationInfo.currentPageIndex);
            // }
        });
        return { tabControl, liveContentContainer, videosContentContainer, videoPurchasesContentContainer };
    }
}
